/**
 * ══════════════════════════════════════════════════════════════════════════════
 * PTEL DESIGN SYSTEM v2.0 - SearchBox Component
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * Input de búsqueda con icono lupa integrado.
 * Migrado desde shared/SearchBox.tsx — API idéntica.
 *
 * Uso:
 *   <SearchBox
 *     value={searchQuery}
 *     placeholder="Buscar infraestructura..."
 *     onChange={setSearchQuery}
 *   />
 *
 * ══════════════════════════════════════════════════════════════════════════════
 */

import React from 'react';
import { colors, typography, borderRadius } from '../design-tokens';

// ─────────────────────────────────────────────────────────────────────────────
// TIPOS
// ─────────────────────────────────────────────────────────────────────────────

export interface SearchBoxProps {
  /** Valor actual del input */
  value?: string;
  /** Placeholder del input */
  placeholder?: string;
  /** Ancho mínimo del componente */
  minWidth?: number | string;
  /** Callback al cambiar el valor */
  onChange?: (value: string) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// ESTILOS
// ─────────────────────────────────────────────────────────────────────────────

const STYLES = {
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    background: colors.bg.secondary,
    border: `1px solid ${colors.border.DEFAULT}`,
    borderRadius: borderRadius.lg,
    minWidth: 180,
  } as React.CSSProperties,

  icon: {
    width: 14,
    height: 14,
    color: colors.fg.muted,
    flexShrink: 0,
  } as React.CSSProperties,

  input: {
    background: 'transparent',
    border: 'none',
    color: colors.fg.primary,
    fontFamily: typography.fontFamily.sans,
    fontSize: typography.fontSize.base,
    outline: 'none',
    width: '100%',
  } as React.CSSProperties,
};

// ─────────────────────────────────────────────────────────────────────────────
// ICONO LUPA
// ─────────────────────────────────────────────────────────────────────────────

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      style={STYLES.icon}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────

export function SearchBox({
  value = '',
  placeholder = 'Buscar infraestructura...',
  minWidth,
  onChange,
}: SearchBoxProps) {
  const containerStyle: React.CSSProperties = {
    ...STYLES.container,
    ...(minWidth !== undefined ? { minWidth } : {}),
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
  };

  return (
    <div style={containerStyle} data-testid="search-box">
      <SearchIcon />
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={handleChange}
        style={STYLES.input}
        data-testid="search-input"
      />
    </div>
  );
}

export default SearchBox;
